/**
 * JecNojoJtrSyncPill
 *
 * Compact header pill that surfaces the live health of the
 *   JapanETCcard → NoJo → JapanTollReceipts
 * data flow to admin/host operators only. Naming intentionally
 * tracks the real service chain (not the abstract "federation"
 * label) so a glance tells you exactly which upstream is wobbling.
 *
 * What you see is the JTR-side inbound leg: the freshness of the
 * NoJo → JapanTollReceipts webhook + pull-mode mirror. JEC → NoJo
 * is upstream of NoJo's own systems; this pill folds JEC into the
 * label since a stale NoJo webhook is the only way a JEC outage
 * surfaces on JTR.
 *
 * Behaviour:
 *   - Only renders for user.role ∈ {admin, host}. Anonymous visitors
 *     and customers never trigger the polling fetch.
 *   - Clicking navigates to /admin/sync-status (the full receipt).
 *   - 60 s poll against /api/testimonials/federation-status which
 *     is fault-soft on the server (200 + degraded_reason on any
 *     internal failure) so the pill can never 5xx the header.
 *   - Hides until first successful response — no flash-of-error.
 *
 * Three-state ladder (driven by the existing endpoint contract):
 *   live (green)  · webhook landed ≤ 30 min ago
 *   pull (amber)  · push idle but pull-mode keeping mirror fresh
 *   cold (red)    · everything stale, serving mirror snapshot
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RadioTower, Activity, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_CONFIG = {
  live: {
    Icon: RadioTower,
    ring: 'ring-emerald-300',
    bg: 'bg-emerald-50 hover:bg-emerald-100',
    text: 'text-emerald-800',
    dot: 'bg-emerald-500',
    pulse: true,
  },
  pull: {
    Icon: Activity,
    ring: 'ring-amber-300',
    bg: 'bg-amber-50 hover:bg-amber-100',
    text: 'text-amber-800',
    dot: 'bg-amber-500',
    pulse: false,
  },
  cold: {
    Icon: AlertCircle,
    ring: 'ring-rose-300',
    bg: 'bg-rose-50 hover:bg-rose-100',
    text: 'text-rose-800',
    dot: 'bg-rose-500',
    pulse: false,
  },
};

function stateWord(state, ja) {
  if (state === 'live') return ja ? '稼働中' : 'live';
  if (state === 'pull') return ja ? 'プル' : 'pull';
  return ja ? 'オフライン' : 'cold';
}

function tooltipFor(data, ja) {
  if (!data) return '';
  const lines = [];
  lines.push(
    ja
      ? 'サービス経路: JapanETCcard → NoJo → JapanTollReceipts'
      : 'Service flow: JapanETCcard → NoJo → JapanTollReceipts',
  );
  if (data.last_webhook_at_jst) {
    lines.push(
      ja
        ? `NoJoからの最終プッシュ: ${data.last_webhook_at_jst}`
        : `Last NoJo push: ${data.last_webhook_at_jst}`,
    );
  }
  if (data.mirror_count !== null && data.mirror_count !== undefined) {
    lines.push(
      ja
        ? `JTRミラー件数: ${data.mirror_count.toLocaleString()}件`
        : `JTR mirror size: ${data.mirror_count.toLocaleString()}`,
    );
  }
  if (data.degraded_reason) {
    lines.push(
      ja
        ? `劣化要因: ${data.degraded_reason}`
        : `Degraded reason: ${data.degraded_reason}`,
    );
  }
  lines.push(ja ? 'クリックで詳細を表示' : 'Click for detail');
  return lines.join('\n');
}

export default function JecNojoJtrSyncPill({ ja = false }) {
  const auth = useAuth() || {};
  const user = auth.user;
  const isAdminOrHost = !!(user && (user.role === 'admin' || user.role === 'host'));
  const [data, setData] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/testimonials/federation-status`, {
        credentials: 'omit',
      });
      if (!r.ok) return;
      const j = await r.json();
      setData(j);
    } catch {
      // Endpoint is server-side fault-soft. Network blip → keep prev state.
    }
  }, []);

  useEffect(() => {
    if (!isAdminOrHost) return undefined;
    fetchStatus();
    const t = setInterval(fetchStatus, 60000);
    return () => clearInterval(t);
  }, [fetchStatus, isAdminOrHost]);

  if (!isAdminOrHost) return null;
  if (!data || !data.state) return null;

  const cfg = STATE_CONFIG[data.state] || STATE_CONFIG.cold;
  const { Icon } = cfg;

  return (
    <Link
      to="/admin/sync-status"
      data-testid="jec-nojo-jtr-sync-pill"
      data-state={data.state}
      data-mode={data.mode}
      title={tooltipFor(data, ja)}
      aria-label={`Sync status: ${stateWord(data.state, ja)}`}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-full ring-1 ring-inset ${cfg.ring} ${cfg.bg} ${cfg.text} relative transition-colors`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
      <span className="absolute -top-0.5 -right-0.5 inline-flex w-2.5 h-2.5">
        {cfg.pulse && (
          <span
            className={`absolute inline-flex h-full w-full rounded-full ${cfg.dot} opacity-50 animate-ping`}
          />
        )}
        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${cfg.dot} ring-2 ring-white`} />
      </span>
    </Link>
  );
}
